import type { LeaderboardEntry, Period } from '@accra/shared';
import { el } from '../components/el';
import { Screen } from '../components/screen';
import { badgeIcons } from '../components/badgeIcons';
import { NameEditor } from './nameEditor';

const TABS: readonly [Period, string][] = [['day', 'Today'], ['week', 'This week'], ['all', 'All time'], ['challenge', '📅 Daily']];
const REFRESH_MS = 15_000;

/** Leaderboard, refreshed while open. Your own row is highlighted. */
export class Leaders extends Screen {
  private period: Period = 'day';
  private readonly list = el('ol', 'leaders-list');
  private readonly tabs = new Map<Period, HTMLButtonElement>();
  private timer = 0;
  private myName: string | null = null;
  private readonly nameEditor: NameEditor;

  constructor(
    parent: HTMLElement,
    private readonly load: (period: Period) => Promise<LeaderboardEntry[] | null>,
    rename: (name: string) => Promise<string | null>,
    onClose: () => void,
  ) {
    super(parent, 'leaders side-panel');
    this.nameEditor = new NameEditor(rename, (name) => {
      this.myName = name;
      void this.refresh();
    });
    const head = el('div', 'garage-head');
    head.append(el('h2', '', 'Leaderboard'), el('span', 'live', '● LIVE'));
    const tabs = el('div', 'tabs');
    for (const [id, label] of TABS) {
      const button = el('button', 'tab', label);
      button.addEventListener('click', () => {
        this.period = id;
        void this.refresh();
      });
      this.tabs.set(id, button);
      tabs.append(button);
    }
    this.panel.append(head, this.nameEditor.root, tabs, this.list, this.button('Done', () => {
      window.clearInterval(this.timer);
      onClose();
    }, 'primary wide'));
  }

  open(myName: string | null): void {
    this.myName = myName;
    this.nameEditor.show(myName);
    this.show();
    void this.refresh();
    window.clearInterval(this.timer);
    this.timer = window.setInterval(() => void this.refresh(), REFRESH_MS);
  }

  private async refresh(): Promise<void> {
    this.tabs.forEach((button, id) => button.classList.toggle('active', id === this.period));
    const rows = await this.load(this.period);
    if (!rows) {
      this.list.replaceChildren(el('li', 'empty', 'Offline: the leaderboard needs a connection.'));
      return;
    }
    if (rows.length === 0) {
      this.list.replaceChildren(el('li', 'empty', this.period === 'challenge' ? "Nobody has played today's challenge yet. Be the first!" : 'Nobody yet. Be the first!'));
      return;
    }
    this.list.replaceChildren(
      ...rows.map((row) => {
        const item = el('li', row.name === this.myName ? 'me' : '');
        const who = el('span', 'name', row.name);
        who.append(badgeIcons(row.badges));
        item.append(el('span', 'rank', `#${row.rank}`), who, el('span', 'score', row.score.toLocaleString()));
        return item;
      }),
    );
    if (!rows.some((row) => row.name === this.myName)) this.list.append(el('li', 'empty', "You're not on this board yet: finish a shift to get on it."));
  }
}
